/**
 * Reduz uma imagem antes de ela virar base64.
 *
 * ── Por que isto existe ───────────────────────────────────────────────────
 *
 * O comprovante ia para o servidor CRU: `readAsDataURL(file)` no arquivo
 * original, sem redução e sem checagem de tamanho. Base64 infla 33%, então
 * uma foto de celular de 6MB vira 8MB de corpo de requisição — e isso já
 * falhava com o limite antigo de 10MB, caindo num `alert('Tente novamente')`
 * que não explicava nada.
 *
 * ── Três ganhos, não um ───────────────────────────────────────────────────
 *
 * 1. Cabe folgado em qualquer teto (uma foto vira ~200-400KB).
 * 2. Sobe muito mais rápido no 4G, que é onde comprovante é fotografado.
 * 3. Barateia o OCR: modelo de visão cobra por área de imagem.
 *
 * ── O limite de 1600px, e por que não menos ───────────────────────────────
 *
 * Comprovante de papel tem letra pequena, e o modelo precisa lê-la. 1600px no
 * lado maior mantém texto de cupom legível; abaixo disso a leitura começa a
 * errar valor, que é o único dado que não pode errar. Se um dia a extração
 * piorar, este número é o primeiro suspeito.
 */

export const LADO_MAXIMO = 1600;
export const QUALIDADE = 0.85;

export interface ImagemReduzida {
  /** Base64 puro, SEM o prefixo `data:` — é o que a API espera. */
  base64: string;
  mimeType: string;
  /** Para log e diagnóstico: quanto encolheu. */
  bytesAntes: number;
  bytesDepois: number;
}

/** As dimensões depois de caber num quadrado de `lado`, mantendo a proporção. */
export const dimensionar = (
  largura: number, altura: number, lado = LADO_MAXIMO,
): { largura: number; altura: number } => {
  const maior = Math.max(largura, altura);
  // Imagem já pequena não é AMPLIADA: aumentar não acrescenta informação e
  // ainda engorda o arquivo.
  if (maior <= lado) return { largura, altura };
  const fator = lado / maior;
  return {
    largura: Math.max(1, Math.round(largura * fator)),
    altura: Math.max(1, Math.round(altura * fator)),
  };
};

/**
 * O núcleo: reduz a partir de um dataUrl.
 *
 * Trabalha com dataUrl e não com `File` de propósito. No formulário, os dois
 * caminhos — enviar a foto inteira e enviar um recorte — passam por um ponto
 * só, e esse ponto tem um dataUrl na mão. Reduzir ali é o que impede um
 * caminho de escapar da redução; reduzir no `File` cobriria só metade, e o
 * recorte (feito num canvas em resolução cheia) sairia grande do mesmo jeito.
 *
 * O recorte continua acontecendo sobre a imagem ORIGINAL, então nenhum detalhe
 * se perde antes da hora — a redução é o último passo antes de enviar.
 */
export const reduzirDataUrl = (
  dataUrl: string,
  bytesAntes = 0,
): Promise<ImagemReduzida> =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.onerror = () => reject(new Error('Esse arquivo não parece uma imagem.'));

    img.onload = () => {
      const { largura, altura } = dimensionar(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = largura;
      canvas.height = altura;

      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Não consegui processar a imagem.'));

      // Fundo branco antes de desenhar: PNG com transparência vira preto no
      // JPEG, e comprovante com fundo preto o modelo não lê.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, largura, altura);
      ctx.drawImage(img, 0, 0, largura, altura);

      // Sempre JPEG na saída, qualquer que seja a entrada: PNG de foto fica
      // maior que o original e desfaz o ganho todo.
      const saida = canvas.toDataURL('image/jpeg', QUALIDADE);
      const base64 = saida.split(',')[1] ?? '';

      resolve({
        base64,
        mimeType: 'image/jpeg',
        bytesAntes,
        // base64 usa 4 caracteres para cada 3 bytes.
        bytesDepois: Math.round(base64.length * 3 / 4),
      });
    };

    img.src = dataUrl;
  });

/** A mesma coisa, partindo de um arquivo. */
export const reduzirImagem = (file: File): Promise<ImagemReduzida> =>
  new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error('Não consegui ler o arquivo.'));
    leitor.onload = () =>
      reduzirDataUrl(leitor.result as string, file.size).then(resolve, reject);
    leitor.readAsDataURL(file);
  });
